import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiClock,
  FiUser,
  FiInfo,
  FiAlertCircle,
} from "react-icons/fi";
import { getAllTours, getTourPriceEstimate } from "../../services/tourService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import HtmlContent from "../common/HtmlContent";
import "../../styles/html-content.css";

const TourSelection = ({
  formData,
  onChange,
  onTourSelect,
  selectedTourId,
}) => {
  // Vehicle capacity ranges for different car types
  const vehicleCapacityRanges = {
    car: { min: 1, max: 3 },
    minivan: { min: 4, max: 7 },
    van: { min: 8, max: 12 },
    minibus: { min: 13, max: 20 },
  };

  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTourId, setActiveTourId] = useState(selectedTourId || null);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [tourDate, setTourDate] = useState(new Date());
  const [tourTime, setTourTime] = useState({ hours: "10", minutes: "00" });
  const [tourPersons, setTourPersons] = useState(formData.persons || 2);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [activeTourDetails, setActiveTourDetails] = useState(null);
  const [showTourDetails, setShowTourDetails] = useState(false);
  const [capacityWarning, setCapacityWarning] = useState(null);
  const [travelGuide, setTravelGuide] = useState({
    available: false,
    time: "",
  });
  // Generate hour options (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  // Generate minute options (00, 15, 30, 45)
  const minuteOptions = ["00", "15", "30", "45"];

  // Function to get suggested vehicle based on number of persons
  const getSuggestedVehicle = (persons) => {
    if (persons >= 1 && persons <= 3) return "car";
    if (persons >= 4 && persons <= 7) return "minivan";
    if (persons >= 8 && persons <= 12) return "van";
    if (persons >= 13 && persons <= 20) return "minibus";
    return "minibus"; // Default for very large groups
  };

  // Function to validate if the person count is within range for selected vehicle
  const validatePersonCount = (persons, vehicleType) => {
    const range = vehicleCapacityRanges[vehicleType] || { min: 1, max: 20 };

    if (persons < range.min) {
      return {
        valid: false,
        message: `This ${vehicleType} requires at least ${range.min} ${
          range.min === 1 ? "person" : "persons"
        }. Please adjust the number of persons or change your vehicle type to "${getSuggestedVehicle(
          persons
        )}".`,
      };
    }

    if (persons > range.max) {
      return {
        valid: false,
        message: `This ${vehicleType} can only accommodate up to ${
          range.max
        } persons. Please reduce the number of persons or change your vehicle type to "${getSuggestedVehicle(
          persons
        )}".`,
      };
    }

    return { valid: true };
  };

  // Check capacity whenever tourPersons or vehicle type changes
  useEffect(() => {
    if (formData.carType && tourPersons) {
      const validation = validatePersonCount(tourPersons, formData.carType);
      setCapacityWarning(validation.valid ? null : validation.message);
    } else {
      setCapacityWarning(null);
    }
  }, [tourPersons, formData.carType]);

  // Fetch all available tours on component mount
  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        const toursData = await getAllTours();
        // Filter to only include active tours
        const activeTours = toursData.filter((tour) => tour.isActive);
        setTours(activeTours);
      } catch (err) {
        setError("Failed to load tours. Please try again later.");
        console.error("Error loading tours:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const TOURS_PER_PAGE = 3;

  // Calculate paginated tours
  const totalPages = Math.ceil(tours.length / TOURS_PER_PAGE);
  const paginatedTours = tours.slice(
    (currentPage - 1) * TOURS_PER_PAGE,
    currentPage * TOURS_PER_PAGE
  );

  // Reset to first page if tours change and current page is out of range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [tours, totalPages]);
  // Get price estimate when tour or parameters change
  useEffect(() => {
    const getPriceEstimate = async () => {
      if (!activeTourId || !tourPersons) {
        setPriceEstimate(null);
        return;
      }

      try {
        setLoadingPrice(true);
        // Use vehicle type from parent form if available
        const vehicleType = formData.carType || "car";
        const result = await getTourPriceEstimate(
          activeTourId,
          tourDate.toISOString(),
          tourPersons,
          vehicleType
        );

        setPriceEstimate(result);
      } catch (err) {
        console.error("Error getting tour price estimate:", err);
        setPriceEstimate(null);
      } finally {
        setLoadingPrice(false);
      }
    };

    getPriceEstimate();
  }, [activeTourId, tourDate, tourPersons, formData.carType]);

  // When active tour changes, update the parent component
  useEffect(() => {
    if (activeTourId) {
      const selectedTour = tours.find((tour) => tour._id === activeTourId);
      setActiveTourDetails(selectedTour);

      // Only pass the tour selection to parent if capacity validation passes
      const validation = formData.carType
        ? validatePersonCount(tourPersons, formData.carType)
        : { valid: true };

      if (validation.valid) {
        onTourSelect({
          tourId: activeTourId,
          tourDate: tourDate.toISOString(),
          tourTime,
          tourPersons,
          tourPrice: priceEstimate?.totalPrice || 0,
          tourName: selectedTour?.name || "",
          travelGuide,
        });
      } else {
        // If validation fails, pass null to indicate invalid selection
        onTourSelect(null);
      }
    } else {
      onTourSelect(null);
    }
  }, [activeTourId, tourDate, tourTime, tourPersons, priceEstimate, tours]);

  // Handle tour selection
  const handleTourSelect = (tourId) => {
    if (activeTourId === tourId) {
      // Clicking on the selected tour unselects it
      setActiveTourId(null);
      setShowTourDetails(false);
    } else {
      // Select a new tour
      setActiveTourId(tourId);
      setShowTourDetails(true);
    }
  };

  // Handle tour details toggle
  const toggleTourDetails = (e, tourId) => {
    e.stopPropagation();

    if (activeTourId === tourId) {
      setShowTourDetails(!showTourDetails);
    } else {
      setActiveTourId(tourId);
      setShowTourDetails(true);
    }
  };

  // Prevent event bubbling for child elements
  const handleFormElementClick = (e) => {
    e.stopPropagation();
  };

  const seasons = {
    low: {
      ranges: [
        { start: "2025-04-01", end: "2025-06-25" },
        { start: "2025-09-16", end: "2025-10-30" },
      ],
      name: "Low Season",
      description: "April 1 - June 25, September 16 - October 30",
    },
    medium: {
      ranges: [
        { start: "2025-06-26", end: "2025-07-10" },
        { start: "2025-08-26", end: "2025-09-15" },
      ],
      name: "Medium Season",
      description: "June 26 - July 10, August 26 - September 15",
    },
    high: {
      ranges: [{ start: "2025-07-11", end: "2025-08-25" }],
      name: "High Season",
      description: "July 11 - August 25",
    },
  };

  // Function to determine the season based on the date
  const determineSeason = (date) => {
    const dateString = date.toISOString().split("T")[0];
    for (const [key, season] of Object.entries(seasons)) {
      for (const range of season.ranges) {
        const start = new Date(range.start);
        const end = new Date(range.end);
        if (date >= start && date <= end) {
          return { key, ...season };
        }
      }
    }
    return { key: "low", ...seasons.low }; // Default to low season if no match
  };

  // Function to get price based on season and vehicle type
  const getSeasonPrice = (tour, selectedDate, vehicleType = "car") => {
    const season = determineSeason(selectedDate);
    const pricing = tour.pricing[vehicleType.toLowerCase()] || tour.pricing.car;

    switch (season.key) {
      case "medium":
        return pricing.mediumSeason || pricing.lowSeason;
      case "high":
        return pricing.highSeason || pricing.mediumSeason || pricing.lowSeason;
      case "low":
      default:
        return pricing.lowSeason;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading available tours...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-8">{error}</div>;
  }

  const handlePersonCountChange = (value) => {
    setTourPersons(value);
    if (formData.carType) {
      const validation = validatePersonCount(value, formData.carType);
      setCapacityWarning(validation.valid ? null : validation.message);
    }
  };

  const handleTravelGuideChange = (e) => {
    const updatedTravelGuide = {
      ...travelGuide,
      [e.target.name]: e.target.value,
    };
    setTravelGuide(updatedTravelGuide);
    
    if (activeTourId) {
      const selectedTour = tours.find((tour) => tour._id === activeTourId);
      onTourSelect({
        tourId: activeTourId,
        tourDate: tourDate.toISOString(),
        tourTime,
        tourPersons,
        tourPrice: priceEstimate?.totalPrice || 0,
        tourName: selectedTour?.name || "",
        travelGuide: updatedTravelGuide,
      });
    }
  };

  return (
    <div className="my-6">
      <h3 className="text-xl font-semibold mb-4 text-gray-300">
        Available Tours
      </h3>
      <p className="text-gray-300 mb-4">
        Enhance your transfer with one of our exclusive guided tours of Athens
      </p>

      {tours.length === 0 ? (
        <div className="text-gray-200 py-4">
          No tours are currently available.
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 gap-4 mb-8">
            {paginatedTours.map((tour) => (
              <div
                key={tour._id}
                className={`border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer
                  ${
                    activeTourId === tour._id
                      ? "border-red-500 border-2 bg-red-50/40"
                      : "border-gray-200"
                  }`}
                onClick={() => handleTourSelect(tour._id)}
              >
                <div className="flex flex-col md:flex-row">
                  {tour.imageUrl && (
                    <div className="w-full md:w-1/3 h-48 md:h-auto">
                      <img
                        src={`${"https://blackleader-server.create-site.gr"}${
                          tour.imageUrl.startsWith("/") ? "" : "/"
                        }${tour.imageUrl}`}
                        alt={tour.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-4 flex-1 bg-gray-600/40">
                    <div className="flex w-full justify-between">
                      <h4 className="text-lg font-semibold text-gray-100">
                        {tour.name}
                      </h4>
                      {activeTourId === tour._id ? (
                        <span className="text-red-600 font-semibold">
                          SELECTED
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center mt-2 text-gray-200">
                      <span className="mr-4">
                        Duration: {tour.duration} hours
                      </span>
                    </div>
                    {/* Use HtmlContent for description preview */}
                    <div className="mt-2 text-sm text-gray-200 line-clamp-2">
                      <HtmlContent
                        html={tour.description}
                        className="text-gray-200"
                      />
                    </div>
                    <div className="mt-3 flex justify-between items-center">
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            toggleTourDetails(e, tour._id);
                          }}
                          className="text-blue-300 text-sm flex items-center"
                        >
                          <FiInfo className="mr-1" />
                          {showTourDetails && activeTourId === tour._id
                            ? "Hide details"
                            : "View details"}
                        </button>
                      </div>
                      <div className="text-blue-600 font-semibold">
                        From €
                        {getSeasonPrice(
                          tour,
                          new Date(),
                          formData.carType
                        ).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tour details section with HtmlContent rendering */}
                {showTourDetails && activeTourId === tour._id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-200 bg-gray-50 p-4"
                    onClick={handleFormElementClick}
                  >
                    <h5 className="font-semibold mb-2">Tour Details</h5>
                    <div className="mb-3">
                      <HtmlContent html={tour.description} />
                    </div>

                    {tour.highlights && tour.highlights.length > 0 && (
                      <div className="mb-3">
                        <h6 className="font-medium mb-1 text-gray-800">
                          Highlights:
                        </h6>
                        <div className="pl-5 text-gray-700">
                          {tour.highlights.map((highlight, idx) => (
                            <div key={idx} className="mb-2">
                              <HtmlContent html={highlight} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {tour.locations && tour.locations.length > 0 && (
                      <div className="mb-3">
                        <h6 className="font-medium mb-1 text-gray-800">
                          Locations:
                        </h6>
                        <div className="pl-5 text-gray-700">
                          {tour.locations.map((location, idx) => (
                            <div key={idx} className="mb-2">
                              <HtmlContent html={location} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rest of tour booking form */}
                    <h5 className="font-semibold mt-4 mb-2">Book This Tour</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          <FiCalendar className="inline mr-1" /> Tour Date
                        </label>
                        <DatePicker
                          selected={tourDate}
                          onChange={(date) => setTourDate(date)}
                          dateFormat="dd/MM/yyyy"
                          wrapperClassName="w-full bg-gray-50 m-auto items-center h"
                          calendarClassName="bg-gray-50"
                          className="w-full px-3 py-[6.5px] items-center border border-gray-300 bg-gray-200 rounded text-sm"
                          minDate={new Date()}
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="block text-sm font-medium mb-1">
                          <FiUser className="inline mr-1" /> Tour Guide
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          value={travelGuide.available ? "yes" : "no"}
                          onChange={(e) =>
                            handleTravelGuideChange({
                              target: {
                                name: "available",
                                value: e.target.value === "yes",
                              },
                          })}
                          required
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>

                        {travelGuide.available && (
                          <div>
                            <label className="block text-sm font-medium my-1">
                              <FiClock className="inline mr-1 mb-1" /> Guide Time
                              Available
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                              placeholder="Guide for how many hours? (e.g. 2h, 3h)"
                              value={travelGuide.time}
                              onChange={(e) =>
                                handleTravelGuideChange({
                                  target: {
                                    name: "time",
                                    value: e.target.value,
                                  },
                                })
                              }
                              required={travelGuide.available}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          <FiUser className="inline mr-1" /> Number of Persons
                        </label>
                        <select
                          className={`w-full px-3 py-2 border rounded text-sm ${
                            capacityWarning
                              ? "border-red-500 bg-red-50"
                              : "border-gray-300"
                          }`}
                          value={tourPersons}
                          onChange={(e) =>
                            handlePersonCountChange(Number(e.target.value))
                          }
                        >
                                  {(() => {
                                    // Determine min and max persons based on vehicle type
                                    let minPersons, maxPersons;
                                    
                                    switch(formData.carType) {
                                      case 'car':
                                        minPersons = 1;
                                        maxPersons = 3;
                                        break;
                                      case 'minivan':
                                        minPersons = 4;
                                        maxPersons = 7;
                                        break;
                                      case 'van':
                                        minPersons = 8;
                                        maxPersons = 12;
                                        break;
                                      case 'minibus':
                                        minPersons = 13;
                                        maxPersons = 20;
                                        break;
                                      default:
                                        minPersons = 1;
                                        maxPersons = 3;
                                    }
                                    
                                    // Check if the selected tour has a maximum passengers limit
                                    const selectedTour = tours.find(tour => tour._id === activeTourId);
                                    if (selectedTour?.maxPassengers && selectedTour.maxPassengers < maxPersons) {
                                      maxPersons = selectedTour.maxPassengers;
                                    }

                                                                        // If current selection is out of new range, adjust it
                                    if (tourPersons < minPersons) {
                                      setTourPersons(minPersons);
                                    } else if (tourPersons > maxPersons) {
                                      setTourPersons(maxPersons);
                                    }

                                    
                                    // Generate options from min to max
                                    const options = [];
                                    for (let i = minPersons; i <= maxPersons; i++) {
                                      options.push(
                                        <option key={i} value={i}>
                                          {i}
                                        </option>
                                      );
                                    }
                                    
                                    return options;
                                  })()}
                        </select>

                        {/* Display capacity warning message with stronger styling */}
                        {capacityWarning && (
                          <div className="mt-2 text-red-600 text-sm font-medium flex items-start bg-red-50 p-2 rounded">
                            <FiAlertCircle className="mr-1 flex-shrink-0 mt-0.5" />
                            <span>{capacityWarning}</span>
                          </div>
                        )}

                        {formData.carType && (
                          <div className="mt-1 text-xs font-medium text-gray-700">
                            {formData.carType === "car" &&
                              "Car capacity: 1-3 persons"}
                            {formData.carType === "minivan" &&
                              "Minivan capacity: 4-7 persons"}
                            {formData.carType === "van" &&
                              "Van capacity: 8-12 persons"}
                            {formData.carType === "minibus" &&
                              "Minibus capacity: 13-20 persons"}
                          </div>
                        )}
                      </div>
                    </div>
                    {loadingPrice ? (
                      <div className="text-center py-2">
                        Calculating price...
                      </div>
                    ) : priceEstimate && !capacityWarning ? (
                      <div className="bg-blue-50 p-3 rounded">
                        <div className="mb-1">
                          <span className="font-medium">
                            {priceEstimate.pricingType === "fixed"
                              ? "Fixed price:"
                              : "Price per person:"}
                          </span>{" "}
                          €{priceEstimate.unitPrice}
                        </div>
                        <div className="text-lg font-semibold text-blue-700">
                          Total: €{priceEstimate.totalPrice}
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        {capacityWarning
                          ? "Please correct the passenger count to see pricing"
                          : "Select dates and number of persons to see pricing"}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-4">
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPage((p) => Math.max(1, p - 1));
                }}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="text-gray-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 font-medium disabled:opacity-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPage((p) => Math.min(totalPages, p + 1));
                }}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TourSelection;
